"use client"

import { useState, useEffect } from "react"
import { Button } from "@medusajs/ui"
import { Adjustments, XMark, ChevronUpMini, ChevronDown } from "@medusajs/icons"
import { SearchFilters } from "../../templates/search-template"
import { HttpTypes } from "@medusajs/types"
import { getCategoryNameById, getHierarchicalCategories } from "@/lib/util/category-helpers"
import { FILTER_TITLE_MAP } from "../search-filters"


interface FloatingFilterPillProps {
  filters: SearchFilters
  onOpenFilters: () => void
  onRemoveFilter: (filters: Partial<SearchFilters>) => void
  onClearAll: () => void
  isVisible: boolean
  categories: HttpTypes.StoreProductCategory[]
}

export default function FloatingFilterPill({
  filters,
  onOpenFilters,
  onRemoveFilter,
  onClearAll,
  isVisible,
  categories,
}: FloatingFilterPillProps) {
  // Add price formatting function
  const formatPrice = (price: number) => {
    return `€${Math.round(price).toLocaleString()}`
  }

  const [showPreview, setShowPreview] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)

  // Calculate active filter count
  const activeFilterCount = Object.entries(filters).reduce(
    (count, [key, value]) => {
      if (key === "priceRange") {
        const [min, max] = value as [number, number]
        if (min > 0 || max < 10000) {
          return count + 1
        }
        return count
      }

      if (Array.isArray(value)) {
        return count + value.length
      }

      if (typeof value === "boolean" && value) {
        return count + 1
      }

      return count
    },
    0
  )

  // Handle haptic feedback (if available)
  const triggerHapticFeedback = () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(50) // Light haptic feedback
    }
  }

  // Handle preview toggle with animation
  const togglePreview = () => {
    triggerHapticFeedback()
    setIsAnimating(true)
    setShowPreview(!showPreview)
    setTimeout(() => setIsAnimating(false), 300)
  }

  if (!isVisible || activeFilterCount === 0) return null

  const removeFilterValue = (
    filterType: keyof Omit<SearchFilters, 'priceRange' | 'inStock' | 'fastDelivery' | 'bulkPricing' | 'programmable'>,
    value: string
  ) => {
    triggerHapticFeedback()
    onRemoveFilter({
      [filterType]: (filters[filterType] as string[]).filter(item => item !== value)
    })
  }

  const removeQuickFilter = (filterType: 'inStock' | 'fastDelivery' | 'bulkPricing') => {
    triggerHapticFeedback()
    onRemoveFilter({
      [filterType]: false
    })
  }

  const removePriceRange = () => {
    triggerHapticFeedback()
    onRemoveFilter({
      priceRange: [0, 10000]
    })
  }

  const handleClearAll = () => {
    triggerHapticFeedback()
    onClearAll()
    setShowPreview(false)
  }

  const handleEditFilters = () => {
    triggerHapticFeedback()
    onOpenFilters()
    setShowPreview(false)
  }

  // Function to get categories for display with correct subcategory logic
  const getDisplayCategories = () => {
    const { mainCategories, subcategoriesByParent } = getHierarchicalCategories(categories)
    
    // Get all selected category IDs
    const selectedCategoryIds = filters.categories
    
    // Separate main categories and subcategories
    const selectedMainCategories = selectedCategoryIds.filter(id => 
      mainCategories.some(mainCat => mainCat.id === id)
    )
    
    // Get all subcategories from the subcategoriesByParent object
    const allSubCategories = Object.values(subcategoriesByParent).flat()
    
    const selectedSubCategories = selectedCategoryIds.filter(id => 
      allSubCategories.some((subCat: HttpTypes.StoreProductCategory) => subCat.id === id)
    )
    
    // If main categories are selected, don't show their subcategories
    // But still show directly selected subcategories
    const displayCategories = [...selectedMainCategories]
    
    // Add subcategories that are not children of selected main categories
    selectedSubCategories.forEach(subCatId => {
      const subCategory = allSubCategories.find((sub: HttpTypes.StoreProductCategory) => sub.id === subCatId)
      if (subCategory && subCategory.parent_category_id) {
        // Check if this subcategory's parent is not in the selected main categories
        const parentIsSelected = selectedMainCategories.includes(subCategory.parent_category_id)
        if (!parentIsSelected) {
          displayCategories.push(subCatId)
        }
      }
    })
    
    return displayCategories
  }

  // Get active filter summary for display
  const getFilterSummary = () => {
    const summaryParts = []
    if (filters.categories && filters.categories.length > 0) summaryParts.push(`${filters.categories.length} kategorij`)
    if (filters.brands && filters.brands.length > 0) summaryParts.push(`${filters.brands.length} blagovnih znamk`)
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 10000) summaryParts.push('cenovni razpon')
    if (filters.fastDelivery) summaryParts.push('samo na zalogi')
    return summaryParts.slice(0, 2).join(', ') + (summaryParts.length > 2 ? '...' : '')
  }

  // Get categories for display
  const displayCategories = getDisplayCategories()

  return (
    <div className={`fixed bottom-16 left-1/2 transform -translate-x-1/2 z-30 lg:hidden transition-all duration-300 ${
      isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
    }`}>
      <div className="relative">
        {/* Filter Preview Popup */}
        {showPreview && (
          <div className={`absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-80 max-w-[90vw] bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 ${
            isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
          }`}>
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Adjustments className="w-4 h-4 text-blue-600" />
                  Aktivni filtri ({activeFilterCount})
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="transparent"
                    size="small"
                    onClick={handleClearAll}
                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                  >
                    Počisti vse
                  </Button>
                  <button
                    onClick={() => setShowPreview(false)}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <XMark className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-1">{getFilterSummary()}</p>
            </div>
            
            <div className="max-h-60 overflow-y-auto">
              <div className="p-4 space-y-3">
                {/* Quick filters */}
                {filters.fastDelivery && (
                  <div className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span className="text-sm font-medium text-blue-800">Samo na zalogi</span>
                    </div>
                    <button
                      onClick={() => removeQuickFilter('fastDelivery')}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-100 transition-colors"
                    >
                      <XMark className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                {filters.bulkPricing && (
                  <div className="flex items-center justify-between py-2 px-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span className="text-sm font-medium text-purple-800">Količinske cene</span>
                    </div>
                    <button
                      onClick={() => removeQuickFilter('bulkPricing')}
                      className="text-purple-600 hover:text-purple-800 p-1 rounded-full hover:bg-purple-100 transition-colors"
                    >
                      <XMark className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                {/* Price range */}
                {(filters.priceRange[0] > 0 || filters.priceRange[1] < 10000) && (
                  <div className="flex items-center justify-between py-2 px-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <span className="text-sm font-medium text-yellow-800">
                        {formatPrice(filters.priceRange[0])} - {formatPrice(filters.priceRange[1])}
                      </span>
                    </div>
                    <button
                      onClick={removePriceRange}
                      className="text-yellow-600 hover:text-yellow-800 p-1 rounded-full hover:bg-yellow-100 transition-colors"
                    >
                      <XMark className="w-3 h-3" />
                    </button>
                  </div>
                )}
                
                {/* Categories - Show main categories and directly selected subcategories */}
                {displayCategories.slice(0, 3).map(category => (
                  <div key={category} className="flex items-center justify-between py-2 px-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0"></div>
                      <span className="text-sm font-medium text-indigo-800 truncate">
                        {getCategoryNameById(category, categories) || category}
                      </span>
                    </div>
                    <button
                      onClick={() => removeFilterValue('categories', category)}
                      className="text-indigo-600 hover:text-indigo-800 p-1 rounded-full hover:bg-indigo-100 transition-colors flex-shrink-0"
                    >
                      <XMark className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {/* Brands */}
                {filters.brands && filters.brands.slice(0, 3).map(brand => (
                  <div key={brand} className="flex items-center justify-between py-2 px-3 bg-pink-50 rounded-lg border border-pink-200">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-pink-500 rounded-full flex-shrink-0"></div>
                      <span className="text-sm font-medium text-pink-800 truncate">
                        {brand}
                      </span>
                    </div>
                    <button
                      onClick={() => removeFilterValue('brands', brand)}
                      className="text-pink-600 hover:text-pink-800 p-1 rounded-full hover:bg-pink-100 transition-colors flex-shrink-0"
                    >
                      <XMark className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                
                {/* Materials */}
                {filters.material && filters.material.slice(0, 3).map(material => (
                  <div key={material} className="flex items-center justify-between py-2 px-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></div>
                      <span className="text-sm font-medium text-orange-800 truncate">
                        {material}
                      </span>
                    </div>
                    <button
                      onClick={() => removeFilterValue('material', material)}
                      className="text-orange-600 hover:text-orange-800 p-1 rounded-full hover:bg-orange-100 transition-colors flex-shrink-0"
                    >
                      <XMark className="w-3 h-3" />
                    </button>
                  </div>
                ))}

                {/* Generic array-based filters (e.g., color/Barva, voltage, etc.) */}
                {Object.entries(filters).map(([key, value]) => {
                  if (
                    [
                      'categories',
                      'brands',
                      'subcategories',
                      'priceRange',
                      'inStock',
                      'fastDelivery',
                      'bulkPricing',
                      'programmable',
                      'material',
                    ].includes(key)
                  ) {
                    return null
                  }
                  if (Array.isArray(value) && value.length > 0) {
                    return value.slice(0, 3).map((item) => (
                      <div key={`${key}-${item}`} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-2 h-2 bg-gray-500 rounded-full flex-shrink-0"></div>
                          <span className="text-sm font-medium text-gray-800 truncate">
                            {((FILTER_TITLE_MAP as any)[key] || key)}: {item}
                          </span>
                        </div>
                        <button
                          onClick={() => removeFilterValue(key as any, item as string)}
                          className="text-gray-600 hover:text-gray-800 p-1 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                        >
                          <XMark className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  }
                  return null
                })}
                
                {/* Show more indicator */}
                {activeFilterCount > 6 && (
                  <div className="text-center py-2">
                    <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                      +{activeFilterCount - 6} več filtrov
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Quick actions */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <Button
                variant="primary"
                size="small"
                onClick={handleEditFilters}
                className="w-full bg-blue-600 hover:bg-blue-700 font-medium"
              >
                Uredi vse filtre
              </Button>
            </div>
          </div>
        )}

        {/* Main Filter Pill */}
        <div className="bg-white rounded-full shadow-lg border border-gray-200 overflow-hidden backdrop-blur-sm">
          <div className="flex items-center">
            <button
              onClick={togglePreview}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-all duration-200 border-r border-gray-200"
            >
              <div className="flex items-center gap-2">
                <Adjustments className="w-3 h-3 text-blue-600" />
                <span className="font-semibold text-gray-900 text-sm">
                  {activeFilterCount}
                </span>
                <span className="text-xs text-gray-600 hidden sm:inline">
                  filter{activeFilterCount !== 1 ? 'i' : ''}
                </span>
              </div>
              {showPreview ? (
                <ChevronDown className="w-3 h-3 text-gray-500" />
              ) : (
                <ChevronUpMini className="w-3 h-3 text-gray-500" />
              )}
            </button>
            
            <Button
              variant="transparent"
              size="small"
              onClick={handleEditFilters}
              className="px-3 py-2 font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-all duration-200"
            >
              Uredi
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
} 